import { useEffect, useState, useCallback } from "react";
import {
    Loader2,
    Check,
    X,
    Trash2,
    Clock,
    Camera,
    Mail,
    User as UserIcon,
    RefreshCcw,
    Filter,
} from "lucide-react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";

const STATUS_LABEL = {
    pending: "Do moderacji",
    approved: "Zatwierdzone",
    rejected: "Odrzucone",
};
const STATUS_COLOR = {
    pending: "#facc15",
    approved: "#22c55e",
    rejected: "#ef4444",
};

/**
 * Admin: Community Screenshots moderation
 * ────────────────────────────────────────
 * Grid of user-submitted screenshots. Filter by status. For each item you can:
 *   • Zatwierdź  → shows up in public gallery
 *   • Odrzuć     → hidden but kept for the record
 *   • Usuń       → hard delete + drop the underlying image from GridFS
 */
export default function CommunityScreenshotsManager() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending"); // default: work queue
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams();
            if (filter) params.set("status", filter);
            const { data } = await api.get(
                `/admin/community-screenshots?${params.toString()}`
            );
            setItems(data || []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const setStatus = async (item, status) => {
        setBusy(item.id);
        try {
            await api.put(`/admin/community-screenshots/${item.id}`, { status });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(null);
        }
    };

    const doDelete = async (item) => {
        if (!window.confirm(`Usunąć zgłoszenie ${item.author_name || "anon"}? Tego nie można cofnąć.`)) return;
        setBusy(item.id);
        try {
            await api.delete(`/admin/community-screenshots/${item.id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-5" data-testid="community-manager">
            <div className="panel-yellow p-5 flex items-center gap-4 flex-wrap">
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--primary)]/20 text-[color:var(--primary)]">
                    <Camera size={18} />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                        Community screenshots
                    </div>
                    <div className="text-white text-sm">
                        Zgłoszenia od użytkowników - zatwierdzone pojawią się publicznie.
                    </div>
                </div>
                <button
                    onClick={load}
                    className="btn-ghost text-xs"
                    disabled={loading}
                >
                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
                    Odśwież
                </button>
            </div>

            {/* Status filter */}
            <div className="flex items-center gap-1 p-1 rounded-md border border-white/10 bg-[color:var(--bg-surface)] w-fit">
                <Filter size={11} className="mx-2 text-[color:var(--text-muted)]" />
                {["pending", "approved", "rejected", ""].map((s) => (
                    <button
                        key={s || "all"}
                        onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 rounded text-[10px] uppercase tracking-[0.15em] transition ${
                            filter === s
                                ? "bg-[color:var(--primary)] text-[#040914] font-bold"
                                : "text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5"
                        }`}
                        data-testid={`community-filter-${s || "all"}`}
                    >
                        {s ? STATUS_LABEL[s] : "Wszystkie"}
                    </button>
                ))}
            </div>

            {error && (
                <div className="text-xs text-red-400" data-testid="community-error">{error}</div>
            )}

            {loading && items.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[color:var(--text-secondary)]">
                    <Loader2 className="animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="p-10 text-center text-sm text-[color:var(--text-muted)] border border-dashed border-white/10 rounded-md">
                    Brak zgłoszeń w tej kategorii.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {items.map((it) => (
                        <ScreenshotCard
                            key={it.id}
                            item={it}
                            busy={busy === it.id}
                            onApprove={() => setStatus(it, "approved")}
                            onReject={() => setStatus(it, "rejected")}
                            onReset={() => setStatus(it, "pending")}
                            onDelete={() => doDelete(it)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ScreenshotCard({ item, busy, onApprove, onReject, onReset, onDelete }) {
    const color = STATUS_COLOR[item.status] || "#9ca3af";
    return (
        <div
            className="rounded-md border border-white/10 bg-[color:var(--bg-surface)] overflow-hidden"
            data-testid={`community-card-${item.id}`}
        >
            <div className="relative aspect-video bg-black/40">
                <img
                    src={resolveMediaUrl(item.image_url)}
                    alt=""
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <span
                    className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.15em] font-bold"
                    style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
                >
                    {STATUS_LABEL[item.status] || item.status}
                </span>
            </div>
            <div className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs text-white">
                    <UserIcon size={12} className="text-[color:var(--text-muted)]" />
                    <span className="font-semibold">
                        {item.author_name || "Anonim"}
                    </span>
                    {item.email && (
                        <span className="text-[10px] text-[color:var(--text-muted)] inline-flex items-center gap-1">
                            <Mail size={10} /> {item.email}
                        </span>
                    )}
                </div>
                {item.description && (
                    <div className="text-xs text-[color:var(--text-secondary)] leading-relaxed line-clamp-3">
                        {item.description}
                    </div>
                )}
                <div className="text-[10px] text-[color:var(--text-muted)] flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(item.created_at).toLocaleString("pl-PL")}
                </div>
                <div className="pt-3 flex flex-wrap gap-2">
                    {item.status !== "approved" && (
                        <button
                            onClick={onApprove}
                            disabled={busy}
                            className="btn-primary text-xs disabled:opacity-50"
                            data-testid={`community-approve-${item.id}`}
                        >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                            Zatwierdź
                        </button>
                    )}
                    {item.status !== "rejected" && (
                        <button
                            onClick={onReject}
                            disabled={busy}
                            className="btn-ghost text-xs disabled:opacity-50"
                            data-testid={`community-reject-${item.id}`}
                        >
                            <X size={12} />
                            Odrzuć
                        </button>
                    )}
                    {item.status !== "pending" && (
                        <button
                            onClick={onReset}
                            disabled={busy}
                            className="btn-ghost text-xs disabled:opacity-50"
                        >
                            Przywróć do kolejki
                        </button>
                    )}
                    <button
                        onClick={onDelete}
                        disabled={busy}
                        className="btn-ghost text-xs text-red-400 hover:text-red-300 ml-auto disabled:opacity-50"
                        data-testid={`community-delete-${item.id}`}
                    >
                        <Trash2 size={12} />
                        Usuń
                    </button>
                </div>
            </div>
        </div>
    );
}
